// Capa de datos del módulo Créditos (préstamos / obligaciones financieras con cuotas).
// Módulo independiente y paralelo a Factoring. Tablas: creditos, creditos_cuotas.
import { crearMovimiento } from "./flujoCaja";
import { confirmarMovimiento } from "./movimientosBancarios";
import { client } from "./supabase";

const PASOS_PERIODICIDAD = {
  mensual: 1,
  trimestral: 3,
  semestral: 6,
  anual: 12,
  unica: 0,
};

// Suma `meses` a una fecha YYYY-MM-DD fijando el día en `diaPago` (clamp al último día del mes).
// Aritmética pura sobre Y/M/D para evitar corrimientos por timezone.
const sumarMeses = (fechaBase, meses, diaPago) => {
  const [y, m, d] = fechaBase.split("-").map(Number);
  // m es 1-based; pasamos a índice 0-based para el cálculo y volvemos
  const totalMes = m - 1 + meses;
  const nuevoAnio = y + Math.floor(totalMes / 12);
  const nuevoMes = ((totalMes % 12) + 12) % 12; // 0-based
  const diaObjetivo = diaPago || d;
  const ultimoDiaMes = new Date(nuevoAnio, nuevoMes + 1, 0).getDate();
  const dia = Math.min(diaObjetivo, ultimoDiaMes);
  const mm = String(nuevoMes + 1).padStart(2, "0");
  const dd = String(dia).padStart(2, "0");
  return `${nuevoAnio}-${mm}-${dd}`;
};

// Genera el schedule de cuotas a partir del header del crédito (sin persistir).
// - fecha_vencimiento: cuota n (1-based) = primera_cuota + (n-1) períodos.
// - monto: monto_cuota_fijo si está; si no, reparto parejo monto_original / num_cuotas.
// - las primeras `cuotas_gracia` cuotas quedan en 0 (período de gracia).
export const generarCuotas = (credito) => {
  const {
    num_cuotas,
    periodicidad,
    dia_pago,
    fecha_primera_cuota,
    monto_cuota_fijo,
    monto_original,
    cuotas_gracia = 0,
  } = credito;

  const paso = PASOS_PERIODICIDAD[periodicidad] ?? 1;
  const n = Number(num_cuotas) || 0;
  const tieneCuotaFija = monto_cuota_fijo != null && monto_cuota_fijo !== "";
  const repartoParejo = n > 0 ? Math.round(Number(monto_original) / n) : 0;
  const montoBase = tieneCuotaFija ? Number(monto_cuota_fijo) : repartoParejo;

  const cuotas = [];
  for (let i = 1; i <= n; i += 1) {
    const fechaVenc = sumarMeses(fecha_primera_cuota, (i - 1) * paso, dia_pago);
    const enGracia = i <= Number(cuotas_gracia || 0);
    cuotas.push({
      numero_cuota: i,
      fecha_vencimiento: fechaVenc,
      monto_total: enGracia ? 0 : montoBase,
      estado: "pendiente",
    });
  }
  return cuotas;
};

// Lista créditos del país con sus cuotas embebidas.
export const getCreditos = async (pais) => {
  const { data, error } = await client
    .from("creditos")
    .select("*, cuotas:creditos_cuotas(*, movimiento:movimientos_bancarios(id, fecha))")
    .eq("pais", pais)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map((c) => ({
    ...c,
    cuotas: (c.cuotas || []).sort((a, b) => a.numero_cuota - b.numero_cuota),
  }));
};

// Crea el header y genera/inserta sus cuotas. Devuelve el crédito con cuotas.
// Créditos 'otorgado' (a favor): el recupero se modela como una única cuota a favor —
// generarCuotas ya produce la cuota (num_cuotas=1, fecha = recupero, monto_total = monto);
// acá se completa el desglose que pide el negocio (monto_capital = monto, monto_interes = 0).
// El camino de deuda ('tomado') queda intacto (cuotas con desglose null, como hasta hoy).
export const crearCreditoConCuotas = async (creditoInput) => {
  const { data: credito, error } = await client.from("creditos").insert(creditoInput).select().single();
  if (error) throw error;

  const esOtorgado = credito.direccion === "otorgado";
  const cuotas = generarCuotas(credito).map((c) => ({
    ...c,
    credito_id: credito.id,
    ...(esOtorgado ? { monto_capital: c.monto_total, monto_interes: 0 } : {}),
  }));
  if (cuotas.length > 0) {
    const { error: errCuotas } = await client.from("creditos_cuotas").insert(cuotas);
    if (errCuotas) {
      // Rollback manual del header para no dejar un crédito sin schedule.
      await client.from("creditos").delete().eq("id", credito.id);
      throw errCuotas;
    }
  }
  return { ...credito, cuotas };
};

// Obtiene las cuotas pendientes de créditos activos para proyectar en FlujoCajaV2.
// JOIN inner a creditos (filtrado por pais + estado activo). Ordenadas por vencimiento.
// AMBAS direcciones (Fase 3): se trae 'tomado' Y 'otorgado'. El `direccion` embebido lo usa
// mapCuotaCredito para el signo — 'tomado' egreso (negativo), 'otorgado' entrada (positiva).
// (La exclusión de 'otorgado' que puso la Fase 2 se revierte acá; se mantiene solo en el path
// de vinculación de egresos — getCreditosConCuotasPendientes.)
export const getCuotasPendientesProyeccion = async (pais) => {
  const { data, error } = await client
    .from("creditos_cuotas")
    .select(
      "id, credito_id, numero_cuota, fecha_vencimiento, monto_total, estado, creditos!inner(id, nombre, moneda, pais, estado, direccion, acreedor_nombre, fecha_desembolso, monto_original)"
    )
    .eq("estado", "pendiente")
    .eq("creditos.pais", pais)
    .eq("creditos.estado", "activo")
    .order("fecha_vencimiento", { ascending: true });
  if (error) throw error;
  return data || [];
};

// Créditos activos del país con sus cuotas PENDIENTES embebidas — input del path "Préstamo"
// del modal de vinculación (vincula un EGRESO a una cuota de deuda). Solo retorna créditos
// que tengan al menos 1 cuota pendiente.
// GUARD (Fase 2, MANTENIDO en Fase 3): solo direccion='tomado'. Un crédito 'otorgado' (a favor)
// se cobra/recupera (ingreso), no se paga; excluirlo acá evita que un egreso se vincule por error
// a su cuota. La Fase 3 revierte la exclusión SOLO en la proyección, no en este path de egresos.
export const getCreditosConCuotasPendientes = async (pais) => {
  const { data, error } = await client
    .from("creditos")
    .select(
      "id, nombre, moneda, pais, acreedor_nombre, estado, cuotas:creditos_cuotas(id, numero_cuota, fecha_vencimiento, monto_total, estado)"
    )
    .eq("pais", pais)
    .eq("estado", "activo")
    .eq("direccion", "tomado")
    .order("nombre");
  if (error) throw error;
  return (data || [])
    .map((c) => ({
      ...c,
      cuotas: (c.cuotas || []).filter((q) => q.estado === "pendiente").sort((a, b) => a.numero_cuota - b.numero_cuota),
    }))
    .filter((c) => c.cuotas.length > 0);
};

// Vincula un movimiento bancario a una cuota de crédito:
//  1. crea el movimiento_caja (pago, categoria_pago='pago_banco', entidad 'otro')
//  2. marca la cuota como pagada + guarda el movimiento_bancario_id
//  3. procesa el movimiento bancario (estado='procesado', movimiento_caja_id, procesado_*)
// Sin rollback automático (mismo criterio que los handlers del modal de vinculación).
export const vincularMovimientoACuota = async ({ movimiento, credito, cuota, pais, userId }) => {
  const movCaja = await crearMovimiento({
    tipo: "pago",
    fecha: movimiento.fecha,
    monto: Math.abs(Number(movimiento.monto || 0)),
    moneda: movimiento.moneda,
    estado: "confirmado",
    cuenta_banco: movimiento.cuenta_bancaria_id,
    entidad_tipo: "otro",
    entidad_id: null,
    entidad_nombre: credito.nombre,
    categoria_pago: "pago_banco",
    movimiento_bancario_id: movimiento.id,
    descripcion: `Pago cuota ${cuota.numero_cuota} - ${credito.nombre}`,
    pais,
    registrado_por: userId || null,
  });
  if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");

  const { error: errCuota } = await client
    .from("creditos_cuotas")
    .update({ estado: "pagada", movimiento_bancario_id: movimiento.id })
    .eq("id", cuota.id);
  if (errCuota) throw errCuota;

  await confirmarMovimiento(movimiento.id, {
    movimiento_caja_id: movCaja.id,
    procesado_por: userId || null,
  });

  return { cajaId: movCaja.id, cuotaId: cuota.id };
};

// Edita el monto de una cuota individual (créditos con schedule manual).
export const actualizarMontoCuota = async (cuotaId, monto) => {
  const { error } = await client
    .from("creditos_cuotas")
    .update({ monto_total: Number(monto) })
    .eq("id", cuotaId);
  if (error) throw error;
};

// Edita notas de una cuota.
export const actualizarNotasCuota = async (cuotaId, notas) => {
  const { error } = await client
    .from("creditos_cuotas")
    .update({ notas: notas?.trim() || null })
    .eq("id", cuotaId);
  if (error) throw error;
};

// Actualiza un crédito 'otorgado' (a favor) manteniendo header ↔ cuota ↔ proyección en SYNC.
// En una sola operación: (a) UPDATE del header y (b) UPDATE de la ÚNICA cuota de recupero, de
// forma que monto_original == cuota.monto_total == cuota.monto_capital y la fecha de recupero
// == cuota.fecha_vencimiento (la fecha que proyecta la caja). Evita el desync que dejaba la
// edición inline de monto (monto_original viejo, monto_capital ≠ monto_total).
// Candados: el UPDATE del header filtra direccion='otorgado' (nunca toca deuda por este camino);
// y solo se permite si la cuota sigue 'pendiente' (no reconciliada/cobrada — reconciliación = Fase 5).
// data: { nombre, acreedor_nombre, monto_original, moneda, pais, fecha_desembolso, fecha_primera_cuota }
export const actualizarCreditoOtorgado = async (creditoId, data) => {
  const { data: cuotas, error: errGet } = await client
    .from("creditos_cuotas")
    .select("id, estado")
    .eq("credito_id", creditoId)
    .order("numero_cuota", { ascending: true });
  if (errGet) throw errGet;
  const cuota = (cuotas || [])[0];
  if (!cuota) throw new Error("El crédito a favor no tiene cuota de recupero");
  if (cuota.estado !== "pendiente") throw new Error("No se puede editar: el recupero ya fue reconciliado");

  const monto = Number(data.monto_original);
  const { error: errHeader } = await client
    .from("creditos")
    .update({
      nombre: data.nombre,
      acreedor_nombre: data.acreedor_nombre,
      monto_original: monto,
      moneda: data.moneda,
      pais: data.pais,
      fecha_desembolso: data.fecha_desembolso,
      fecha_primera_cuota: data.fecha_primera_cuota,
    })
    .eq("id", creditoId)
    .eq("direccion", "otorgado");
  if (errHeader) throw errHeader;

  const { error: errCuota } = await client
    .from("creditos_cuotas")
    .update({
      monto_total: monto,
      monto_capital: monto,
      monto_interes: 0,
      fecha_vencimiento: data.fecha_primera_cuota,
    })
    .eq("id", cuota.id);
  if (errCuota) throw errCuota;

  return { creditoId, cuotaId: cuota.id };
};

// Marca un crédito como liquidado.
export const liquidarCredito = async (creditoId) => {
  const { error } = await client.from("creditos").update({ estado: "liquidado" }).eq("id", creditoId);
  if (error) throw error;
};

// Elimina un crédito (cascade borra sus cuotas).
export const eliminarCredito = async (creditoId) => {
  const { error } = await client.from("creditos").delete().eq("id", creditoId);
  if (error) throw error;
};
